import React from 'react';
import { Stack, TextField, Typography } from '@mui/material';
import { InfoRounded } from '@mui/icons-material';
import IDKeys from '../../constants/idKeys';

/**
 * A text field for entering a PIN value.
 * @param {object} props Props accepted by the component.
 * @param {string} props.value Value of the input.
 * @param {Function} props.onChange Callback that is called when the input's value changes.
 * @param {boolean} props.showValue Whether to mask the passcode.
 * @param {boolean} props.disabled Whether the field is disabled.
 * @param {string} props.errorMessage The error message to display, if any.
 * @param {string} props.labelledBy Sets the `aria-labelledby` value of the MUI `TextField` component.
 * @param {number} props.maxLength Limits the maximum number of characters that can be entered.
 * @returns {React.FunctionComponent} React element that contains the PIN input field.
 */
function PINInput({
  value,
  onChange,
  showValue = true,
  disabled = false,
  errorMessage = '',
  labelledBy,
  maxLength,
}) {
  const handleInputEvent = (e) =>
    onChange(
      e.target.value?.replaceAll(/[^0-9]/g, '').substring(0, maxLength) || '',
    );

  return (
    <Stack>
      <TextField
        id={`${IDKeys.PIN_ENTRY}TextField`}
        autoComplete="off"
        aria-label="pin-digit-input"
        aria-labelledby={labelledBy}
        disabled={disabled}
        type={showValue ? 'text' : 'password'}
        inputProps={{
          autocomplete: 'new-password',
          pattern: '[0-9]*',
          inputMode: 'numeric',
          maxLength,
        }}
        onChange={handleInputEvent}
        value={disabled ? '' : value}
        error={errorMessage?.length > 0}
        sx={{
          width: '300px',
        }}
      />
      <Stack direction="row" justifyContent="space-between">
        {/* Error message if exists */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          color="error.main"
        >
          {errorMessage && (
            <>
              <InfoRounded fontSize="18px" />
              <Typography
                role="alert"
                aria-live="assertive"
                aria-atomic
                id={`${IDKeys.PIN_ENTRY}ErrorText`}
                variant="body_sm"
              >
                {errorMessage}
              </Typography>
            </>
          )}
        </Stack>
        {/* Input counter */}
        <Typography
          id={`${IDKeys.PIN_ENTRY}PINLengthText`}
          component="span"
          alignSelf="flex-end"
          sx={{ color: 'text.secondary' }}
        >
          {maxLength && `${value.length}/${maxLength}`}
        </Typography>
      </Stack>
    </Stack>
  );
}

export default PINInput;
